import { useOutreachRates } from '@discovr/features';
import type { OutreachRatesRow } from '@discovr/types';
import { DataTable, type ColumnDef } from '../../../components/common/DataTable';
import { cn } from '../../../lib/utils';
import { AMBER_PILL } from './brandPill';
import { creatorColumn } from './crmCreatorColumns';
import { CrmListEmpty } from './CrmListEmpty';
import { CrmListFrame } from './CrmListFrame';
import { useCrmListPaging, type CrmCreatorsListProps } from './useCrmListPaging';
import { QUOTE_AFTER_LOCK_LABEL, brandStateLabel, lockedByText, rateLines, revisedLabel, type RateLine } from './crmRatesModel';
import { PILL_BASE } from './OutreachStatusPill';

const UNKNOWN = '—';
const TEXT = 'text-body-sm-regular text-foreground';

/** One column of a creator's stacked rate lines, so each format's quote and lock sit on the same line across columns. */
function RateLineStack({ row, pick }: { row: OutreachRatesRow; pick: (line: RateLine) => string }) {
  return (
    <ul className="space-y-1">
      {rateLines(row).map((line) => (
        <li key={line.key} className={cn(TEXT, 'tabular-nums')}>
          {pick(line)}
        </li>
      ))}
    </ul>
  );
}

/** Who locked the latest rate and when, flagged when it was revised or the creator quoted again after it. */
function LockedByCell({ row }: { row: OutreachRatesRow }) {
  const revised = revisedLabel(row.revisions);
  return (
    <div className="flex flex-col items-start gap-1">
      <span className={TEXT}>{lockedByText(row)}</span>
      {revised && <span className={cn(PILL_BASE, 'bg-stone-200 text-stone-600')}>{revised}</span>}
      {row.quoteAfterLock && <span className={cn(PILL_BASE, AMBER_PILL)}>{QUOTE_AFTER_LOCK_LABEL}</span>}
    </div>
  );
}

const ALL_RATES_COLUMNS: ColumnDef<OutreachRatesRow>[] = [
  creatorColumn<OutreachRatesRow>(),
  { id: 'format', header: 'Format', minWidth: 120, cell: (row) => <RateLineStack row={row} pick={(line) => line.format} /> },
  { id: 'quoted', header: 'Quoted', minWidth: 110, cell: (row) => <RateLineStack row={row} pick={(line) => line.quoted} /> },
  { id: 'locked', header: 'Locked', minWidth: 110, cell: (row) => <RateLineStack row={row} pick={(line) => line.locked} /> },
  { id: 'locked_by', header: 'Locked by / when', minWidth: 200, wrap: true, cell: (row) => <LockedByCell row={row} /> },
  { id: 'brand', header: 'Brand', minWidth: 110, cell: (row) => <span className={TEXT}>{brandStateLabel(row.brandState)}</span> },
  // The close is not built yet, so no creator has an agreement to show.
  { id: 'agreement', header: 'Agreement', minWidth: 100, cell: () => <span className="text-muted-foreground">{UNKNOWN}</span> },
];

/** Columns held back until the close step exists; empty this list to show them again. */
export const HIDDEN_RATES_COLUMNS: readonly string[] = ['agreement', 'signed', 'contact_to_signed'];

const RATES_COLUMNS = ALL_RATES_COLUMNS.filter((column) => !HIDDEN_RATES_COLUMNS.includes(column.id));

/** Every creator who quoted or has a rate locked, latest rate activity first. */
export function CrmRatesTable({ campaignId, query, onClearSearch }: CrmCreatorsListProps) {
  const paging = useCrmListPaging();
  const { data, isLoading, error } = useOutreachRates(campaignId, {
    page: paging.page,
    limit: paging.pageSize,
    q: query || undefined,
  });

  return (
    <CrmListFrame error={error} total={data?.total ?? 0} paging={paging}>
      <DataTable<OutreachRatesRow>
        columns={RATES_COLUMNS}
        data={data?.rows ?? []}
        getRowId={(row) => row.entryId}
        loading={isLoading}
        emptyState={<CrmListEmpty view="rates" query={query} onClearSearch={onClearSearch} />}
      />
    </CrmListFrame>
  );
}
